import type {
  TranslationCatalog,
  TranslationCatalogCreatePayload,
  TranslationCatalogPatch,
} from '~/components/CustomerCommunication/TranslationCatalog/types';

import { json_headers, request } from '~/services/common';

import { makeCustomerCommunicationV1URL } from './customerCommunicationRequestUtils';

type CatalogListResponse = Array<TranslationCatalog> | { result: Array<TranslationCatalog> };
type CatalogResponse = TranslationCatalog | { result: TranslationCatalog };

function unwrapCatalogList(response: CatalogListResponse): Array<TranslationCatalog> {
  return Array.isArray(response) ? response : response.result;
}

function unwrapCatalog(response: CatalogResponse): TranslationCatalog {
  return 'result' in response ? response.result : response;
}

function buildCatalogUrl(path: `/${string}`): URL {
  return makeCustomerCommunicationV1URL(path);
}

export async function requestGetTranslationCatalogs(signal?: AbortSignal): Promise<Array<TranslationCatalog>> {
  const url = buildCatalogUrl('/translation-catalogs');

  const response = await request(url.toString(), {
    method: 'GET',
    credentials: 'include',
    headers: json_headers,
    signal,
  });

  return unwrapCatalogList(response);
}

export async function requestCreateTranslationCatalog(
  payload: TranslationCatalogCreatePayload,
): Promise<TranslationCatalog> {
  const url = buildCatalogUrl('/translation-catalogs');

  const response = await request(url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers: json_headers,
    body: JSON.stringify(payload),
  });

  return unwrapCatalog(response);
}

export async function requestGetTranslationCatalog(id: string, signal?: AbortSignal): Promise<TranslationCatalog> {
  if (!id) {
    throw new Error('missing catalog id');
  }

  const url = buildCatalogUrl(`/translation-catalogs/${id}`);

  const response = await request(url.toString(), {
    method: 'GET',
    credentials: 'include',
    headers: json_headers,
    signal,
  });

  return unwrapCatalog(response);
}

export async function requestPatchTranslationCatalog(id: string, payload: TranslationCatalogPatch) {
  if (!id) {
    throw new Error('missing catalog id');
  }

  const url = buildCatalogUrl(`/translation-catalogs/${id}`);

  const response = await request(url.toString(), {
    method: 'PATCH',
    credentials: 'include',
    headers: json_headers,
    body: JSON.stringify(payload),
  });

  return unwrapCatalog(response);
}

export async function requestDeleteTranslationCatalog(id: string): Promise<void> {
  if (!id) {
    throw new Error('missing catalog id');
  }

  const url = buildCatalogUrl(`/translation-catalogs/${id}`);

  await request(url.toString(), {
    method: 'DELETE',
    credentials: 'include',
    headers: json_headers,
  });
}

export async function requestDeleteTranslationCatalogField(id: string, fieldName: string): Promise<TranslationCatalog> {
  if (!id) {
    throw new Error('missing catalog id');
  }
  if (!fieldName) {
    throw new Error('missing field name');
  }

  const url = buildCatalogUrl(`/translation-catalogs/${id}/fields/${encodeURIComponent(fieldName)}`);

  const response = await request(url.toString(), {
    method: 'DELETE',
    credentials: 'include',
    headers: json_headers,
  });

  return unwrapCatalog(response);
}

export async function requestTranslateTranslationCatalog(id: string): Promise<TranslationCatalog> {
  if (!id) {
    throw new Error('missing catalog id');
  }

  const url = buildCatalogUrl(`/translation-catalogs/${id}/translate`);

  const response = await request(url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers: json_headers,
    body: JSON.stringify({}),
  });

  return unwrapCatalog(response);
}

export async function requestLockTranslationCatalog(id: string): Promise<TranslationCatalog> {
  if (!id) {
    throw new Error('missing catalog id');
  }

  const url = buildCatalogUrl(`/translation-catalogs/${id}/lock`);

  const response = await request(url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers: json_headers,
  });

  return unwrapCatalog(response);
}

export async function requestUploadTranslationCatalog(id: string): Promise<TranslationCatalog> {
  if (!id) {
    throw new Error('missing catalog id');
  }

  const url = buildCatalogUrl(`/translation-catalogs/${id}/upload`);

  const response = await request(url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers: json_headers,
  });

  return unwrapCatalog(response);
}

export async function requestUnlockTranslationCatalog(id: string): Promise<TranslationCatalog> {
  if (!id) {
    throw new Error('missing catalog id');
  }

  const url = buildCatalogUrl(`/translation-catalogs/${id}/unlock`);

  const response = await request(url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers: json_headers,
  });

  return unwrapCatalog(response);
}
